import camelcase from 'camelcase-keys';

import Analytics from 'app/lib/Analytics/Analytics';
import DataAdapter, { ICardData } from 'app/lib/provider/services/data-adapter/DataAdapter';
import { guard } from 'app/utils';

import { ResponseData, ResponseError, IProviderResponse } from '../../utils';
import type {
  IOriginData,
  IOriginDataError,
  IOriginDataPagePromo,
  IOriginDataPageEnd,
} from './types';

const request = async (
  cardType: string,
  cardParam: string,
  cardCode: string,
  tryCount = 1,
): Promise<IOriginData & IOriginDataError> => {
  const url = `${process.env.REACT_APP_REST_API_ADDRESS}/api/v1/mobigrams/web_player/${cardParam}/${cardCode}`;
  const fetchOptions = {
    method: 'get',
    cache: 'no-cache',
  } as const;
  const response: IOriginData & IOriginDataError = await fetch(url, fetchOptions)
    .then((res) => res.json())
    .then((json) => camelcase(json, { deep: true }));

  if (response?.error && tryCount < 5) {
    Analytics.track().errorRequestData(tryCount, response.error);
    await new Promise((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, 100 * tryCount);
    });
    return request(cardType, cardParam, cardCode, tryCount + 1);
  }

  return response;
};

const getOriginData = async (
  cardType: string,
  cardParam: string,
  cardCode: string,
  options?: {
    pages?: number[] | null,
    mode?: string,
  },
): Promise<IOriginData> => {
  const data = await request(cardType, cardParam, cardCode);

  if (data?.error) {
    throw new Error(data?.error);
  }

  if (Array.isArray(data.pages)) {
    data.pages = data.pages.sort((a, b) => {
      if (a.order > b.order) {
        return 1;
      }
      if (a.order < b.order) {
        return -1;
      }
      return 0;
    });
  }

  data.webUrl = !data?.webUrl
    ? `${process.env.REACT_APP_SELF_ADDRESS}/${cardType}/${cardParam}/${cardCode}`
    : data?.webUrl?.replace('/m/', `/${cardType}/`);

  /** @todo should be removed after finish refactoring { */
  data.isB2b = true;
  /** } */
  data.pages.forEach((page, key) => {
    if (guard.isOriginCoverPage(page)) {
      data.pages[key].type = 'message';
    }
    if (guard.isOriginPromoPage(page)) {
      data.pages[key].type = 'action';
    }
    if (guard.isOriginMessagePage(page) && 'eventWidget' in page && typeof page.eventWidget?.position === 'string') {
      const { eventWidget } = page;
      const position: any = {};
      (eventWidget.position as unknown as string)
        .replace(/^--- !ruby\/hash:Hashie::Mash\n([^!]*)\n$/m, '$1')
        .split(/\n/).forEach((item) => {
          const buff = item.replace(' ', '').split(':');
          position[buff[0]] = Number(buff[1]);
        });
      eventWidget.position = position;
    }
    if (guard.isOriginMessagePage(page) && 'buttonWidget' in page && typeof page.buttonWidget?.position === 'string') {
      const { buttonWidget } = page;
      const position: any = {};
      (buttonWidget.position as unknown as string)
        .replace(/^--- !ruby\/hash:Hashie::Mash\n([^!]*)\n$/m, '$1')
        .split(/\n/).forEach((item) => {
          const buff = item.replace(' ', '').split(':');
          position[buff[0]] = Number(buff[1]);
        });
      buttonWidget.position = position;
    }
  });
  const promoAdAdminIndex = data.pages.findIndex((item) => ['promo_ad_admin', 'ad'].includes(item.type));
  const pageEnd: IOriginDataPageEnd = {
    type: 'end',
    order: data.pages.length,
    info: {
      hasReply: !data?.shouldHideReplyPage,
      hasBirthday: !data.wasWebOptInViewed && data.webOptInType === 'birthday',
      hasHoliday: !data.wasWebOptInViewed && data.webOptInType === 'holiday',
      hasPromoTSO: data.webOptInType === 'Promo_TSO',
      isShareableLink: !!data?.isShareableLink,
    },
  };
  if (promoAdAdminIndex > -1) {
    const [endPagePromoAd] = data.pages.splice(promoAdAdminIndex, 1);
    pageEnd.promo = (endPagePromoAd as IOriginDataPagePromo);
  }
  data.pages.push(pageEnd);

  const showOnlyPages = options?.pages || [];
  const isPreviewMode = options?.mode === 'preview';
  data.pages = data.pages.filter((page, index) => {
    if (index === 0) {
      return true;
    }
    if (page.type === 'end' && isPreviewMode) {
      return false;
    }
    if (page.type === 'message' && showOnlyPages.length > 0 && page.id && !showOnlyPages.includes(page.id)) {
      return false;
    }
    return true;
  });

  return data;
};

export default async (
  type: string,
  param: string,
  code: string,
  options?: {
    pages?: number[] | null,
    mode?: string,
  },
): Promise<IProviderResponse<ICardData>> => {
  if (!type || !param || !code) {
    return ResponseError('No card data');
  }

  let originData;

  try {
    originData = await getOriginData(type, param, code, options);
  } catch (error) {
    return ResponseError((error as Error).message);
  }

  const dataAdapter = new DataAdapter();
  const result = await dataAdapter.prepare(originData);

  return ResponseData(result);
};
